import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Repeat, Calendar as CalIcon, Pause, Play, CheckCircle2, XCircle, Clock, Zap } from "lucide-react";
import { toast } from "sonner";

const FREQ_LABEL = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly" };
const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" });

export default function CustomerServices() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/subscriptions/mine").then((r) => setSubs(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    const autopaySub = params.get("autopay");
    if (!sid) return;
    if (autopaySub) {
      api.post(`/subscriptions/${autopaySub}/confirm-autopay`, { session_id: sid })
        .then(() => toast.success("Auto-pay is on! Upcoming visits will be charged automatically."))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
        .finally(() => { setParams({}, { replace: true }); load(); });
      return;
    }
    api.get(`/payments/status/${sid}`).then((r) => {
      if (r.data.payment_status === "paid") toast.success("Visit paid & booked!");
    }).finally(() => { setParams({}, { replace: true }); load(); });
  }, [params, setParams, load]);

  const setupAutopay = async (subId) => {
    try {
      const { data } = await api.post(`/subscriptions/${subId}/setup-autopay`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const disableAutopay = async (subId) => {
    try { await api.post(`/subscriptions/${subId}/disable-autopay`, {}); toast.success("Auto-pay turned off"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const payVisit = async (subId, date) => {
    try {
      const { data } = await api.post(`/subscriptions/${subId}/pay-visit`, { date, origin_url: window.location.origin });
      if (data.free) { toast.success("Visit booked!"); load(); }
      else if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pause = async (subId, date) => {
    try { await api.post(`/subscriptions/${subId}/pause`, { date }); toast.success("Visit paused"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resume = async (subId, date) => {
    try { await api.post(`/subscriptions/${subId}/resume`, { date }); toast.success("Visit resumed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cancel = async (subId) => {
    if (!window.confirm("Cancel this recurring service? Upcoming visits will stop.")) return;
    try { await api.post(`/subscriptions/${subId}/cancel`, { reason: "" }); toast.success("Subscription cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">My recurring services</h1>
      <p className="text-muted-foreground mt-2">Ongoing services you've subscribed to. Pay each visit as it comes up, or pause a date you don't need.</p>

      {subs.length === 0 ? (
        <div className="mt-10 border border-border rounded-sm bg-white p-10 text-center" data-testid="subs-empty">
          <Repeat className="w-10 h-10 text-muted-foreground mx-auto" strokeWidth={1.5} />
          <p className="mt-4 text-muted-foreground">You haven't subscribed to any recurring services yet.</p>
          <Link to="/browse"><Button className="rounded-sm mt-5" data-testid="browse-services-btn">Browse services</Button></Link>
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {subs.map((s) => (
            <div key={s.id} className="border border-border rounded-sm bg-white" data-testid={`sub-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 p-5 border-b border-border">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 rounded-sm px-1.5 py-0.5 inline-flex items-center gap-1"><Repeat className="w-3 h-3" />{FREQ_LABEL[s.frequency]}</span>
                    {s.status === "cancelled" && <span className="text-[10px] uppercase tracking-wider font-bold text-red-700 bg-red-100 rounded-sm px-1.5 py-0.5">Cancelled</span>}
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-2">{s.service_title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    with <Link to={`/p/${s.subscriber_id}`} className="hover:text-primary hover:underline">{s.subscriber_name}</Link> · {s.start_time} · ${s.price}/visit
                  </p>
                  {s.status === "cancelled" && s.cancel_reason && (
                    <p className="text-xs text-red-700 mt-1">Reason: {s.cancel_reason}</p>
                  )}
                </div>
                {s.status === "active" && (
                  <div className="flex items-center gap-2">
                    {s.auto_pay ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-sm px-2 py-1" data-testid={`autopay-on-${s.id}`}>
                        <Zap className="w-3.5 h-3.5" />Auto-pay on
                        <button onClick={() => disableAutopay(s.id)} className="ml-1 underline hover:text-green-900" data-testid={`autopay-off-${s.id}`}>turn off</button>
                      </span>
                    ) : s.price > 0 ? (
                      <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => setupAutopay(s.id)} data-testid={`setup-autopay-${s.id}`}>
                        <Zap className="w-3.5 h-3.5 mr-1" />Set up auto-pay
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" className="rounded-sm h-8 text-destructive border-destructive/40 hover:bg-destructive/5" onClick={() => cancel(s.id)} data-testid={`cancel-sub-${s.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                    </Button>
                  </div>
                )}
              </div>
              {s.status === "active" && (
                <div className="p-5">
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground flex items-center gap-1.5"><CalIcon className="w-3.5 h-3.5" />Upcoming visits</p>
                  <div className="mt-3 divide-y divide-border">
                    {(s.occurrences || []).map((o) => (
                      <div key={o.date} className="flex items-center justify-between py-2.5" data-testid={`occ-${s.id}-${o.date}`}>
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${o.status === "paused" ? "line-through text-muted-foreground" : ""}`}>{fmtDate(o.date)}</span>
                          {o.status === "paid" && <span className="text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Paid</span>}
                          {o.status === "paused" && <span className="text-xs text-amber-700 inline-flex items-center gap-1"><Pause className="w-3.5 h-3.5" />Paused</span>}
                          {o.status === "due" && <span className="text-xs text-primary font-medium inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Due next</span>}
                          {o.status === "upcoming" && <span className="text-xs text-muted-foreground">Upcoming</span>}
                        </div>
                        <div className="flex gap-2">
                          {o.status === "due" && (
                            <Button size="sm" className="rounded-sm h-8" onClick={() => payVisit(s.id, o.date)} data-testid={`pay-${s.id}-${o.date}`}>
                              Pay ${s.price}
                            </Button>
                          )}
                          {(o.status === "due" || o.status === "upcoming") && (
                            <Button size="sm" variant="outline" className="rounded-sm h-8" disabled={!o.can_pause} onClick={() => pause(s.id, o.date)}
                              title={o.can_pause ? "" : `Pause at least ${s.pause_notice_days} day(s) ahead`} data-testid={`pause-${s.id}-${o.date}`}>
                              <Pause className="w-3.5 h-3.5 mr-1" />Pause
                            </Button>
                          )}
                          {o.status === "paused" && (
                            <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => resume(s.id, o.date)} data-testid={`resume-${s.id}-${o.date}`}>
                              <Play className="w-3.5 h-3.5 mr-1" />Resume
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">You can pause an upcoming visit up to {s.pause_notice_days} day(s) before it happens.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
